import { useCallback, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { validateFiles, DEFAULT_VALIDATION_OPTIONS } from './fileValidation';
import type { FileValidationOptions, FileValidationError } from './types';

export interface ImageUploadProps {
  onUpload: (files: File[]) => Promise<void>;
  isUploading: boolean;
  maxFiles?: number;
  maxSizeMB?: number;
  accept?: string;
}

/**
 * Reusable image upload component with drag-and-drop support
 * - Drag and drop files
 * - Click to browse
 * - Preview selected files before upload
 * - File validation with error messages
 * - Upload progress indicator
 */
export function ImageUpload({
  onUpload,
  isUploading,
  maxFiles = 10,
  maxSizeMB = 10,
  accept = 'image/*,application/pdf',
}: ImageUploadProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FileValidationError[]>([]);

  const handleFiles = useCallback(
    (files: File[]) => {
      // Clear previous errors
      setValidationErrors([]);

      // Check max files limit
      if (selectedFiles.length + files.length > maxFiles) {
        setValidationErrors([
          {
            file: files[0],
            error: `Maximum ${maxFiles} files allowed`,
          },
        ]);
        return;
      }

      // Validate files
      const validationOptions: FileValidationOptions = {
        maxSizeMB,
        allowedTypes: DEFAULT_VALIDATION_OPTIONS.allowedTypes,
      };
      const errors = validateFiles(files, validationOptions);

      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Add to selected files
      setSelectedFiles((prev) => [...prev, ...files]);
    },
    [selectedFiles.length, maxFiles, maxSizeMB]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    },
    [handleFiles]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const files = Array.from(e.target.files);
        handleFiles(files);
      }
    },
    [handleFiles]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      await onUpload(selectedFiles);
      // Clear selected files on success
      setSelectedFiles([]);
      setValidationErrors([]);
    } catch {
      // Error handling is done by parent component
    }
  };

  return (
    <div className="space-y-4">
      {/* Drag and drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative rounded-lg border-2 border-dashed p-8 text-center transition-colors
          ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}
          ${isUploading ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:border-gray-400'}
        `}
      >
        <input
          type="file"
          id="file-input"
          multiple
          accept={accept}
          onChange={handleFileInput}
          disabled={isUploading}
          className="hidden"
        />

        <label htmlFor="file-input" className="cursor-pointer">
          <div className="flex flex-col items-center gap-2">
            {isUploading ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
                <p className="text-sm font-medium text-gray-700">Uploading...</p>
              </>
            ) : isDragOver ? (
              <>
                <Upload className="h-10 w-10 text-blue-500" />
                <p className="text-sm font-medium text-gray-700">Drop files here</p>
              </>
            ) : (
              <>
                <Upload className="h-10 w-10 text-gray-400" />
                <p className="text-sm font-medium text-gray-700">
                  Drag files here or click to browse
                </p>
                <p className="text-xs text-gray-500">
                  Supports JPG, PNG, GIF, PDF (max {maxSizeMB}MB, up to {maxFiles} files)
                </p>
              </>
            )}
          </div>
        </label>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="rounded-md bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800">Error:</p>
          <ul className="mt-1 space-y-1 text-sm text-red-700">
            {validationErrors.map((error, index) => (
              <li key={index}>
                {error.file.name}: {error.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Selected files preview */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Selected files ({selectedFiles.length}):
          </p>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
              >
                <div className="flex-1 truncate">
                  <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(index)}
                  disabled={isUploading}
                  className="ml-2 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Upload button */}
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading || selectedFiles.length === 0}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                `Upload ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
